import Polygon from 'polygon';
import { state } from '__common/store';
import { recheckExposureForNewPanelsNeighbours } from '__common/components/exposure/multiPanelsExposureStateOutsideOfEditor';
import { exposedDistance, getRoofZonesAreasMemoized as getRoofZonesAreas} from '__editor/panelsEditor/components/roofZones/roofZones';
import { isEdgePoint } from '__editor/panelsEditor/components/roofZones/utils/edges';
import { getStage } from '../../../__editor/panelsEditor/components/stage/stage';
import { DEBUG } from 'debug';
export const reCheckPanelsExposure = (panelsInCurrentState: panelInState[], panelIds: number[], roofId: number) => {
  const {
    settings: {
      columnSpacing,
      rowSpacing,
    },
    background: {
      bgXY,
      metersPerPixel,
      roofEdges,
      roofEdgesPixiCords,
      zoom, cords,
      rotationDegrees },
    tiltedRoof: {
      roofPitch,
    },
    projectConfiguration: {
      productId,
      projectEnvConfig: {
        tilt,
      },
    },
    roofsSelector: {
      mapType,
    },
  } = state();

  return recheckExposureForNewPanelsNeighbours(
    panelsInCurrentState,
    panelIds,
    roofEdges,
    roofId,
    cords,
    zoom,
    rotationDegrees,
    metersPerPixel,
    columnSpacing,
    rowSpacing,
    bgXY,
    roofPitch,
    productId,
    mapType,
    roofEdgesPixiCords,
    tilt,
  );
};

// Parts of the panel edge covered by the roof boundary, as t in [0, 1] from edge[0] to edge[1].
export const getEdgeCoverageByRoofBoundary = (
  edge: { x: number, y: number }[],
  roofEdges: google.maps.LatLngLiteral[],
  roofId: number,
  cords: cordPoint,
  zoom: number,
  bgRotationDegrees: number,
  bgOffset: pixelPoint,
  insideOfPolygon: boolean,
  roofPitch: string,
  metersPerPixel: number,
  side: 'N' | 'S' | 'E' | 'W',
  roofEdgesPixiCords,
  productId: number,
  mapType: string,
  tilt: number,
): ParamInterval[] => {

  const bgXOffset = bgOffset.x;
  const bgYOffset = bgOffset.y;
  const distance = exposedDistance(metersPerPixel);
  const rotatedRoofZone =  getRoofZonesAreas(
    {roofEdges, roofCenter: cords,  roofEdgesPixiCords, roofPitch, roofId, distance, zoom, rotationDegrees: bgRotationDegrees, bgOffSet: {x: bgXOffset, y: bgYOffset}, insideOfPolygon, productId, tilt, mapType,});
  let coverage: ParamInterval[] = [];

  if (Object.keys(rotatedRoofZone).length) {
    const { zoneAreas, zonePolygons, coverageAreas } = getSideGeometry(rotatedRoofZone, side, distance);

    const firstPoint = { x: edge[0].x + bgXOffset, y: edge[0].y + bgYOffset };
    const secondPoint = { x: edge[1].x + bgXOffset, y: edge[1].y + bgYOffset };
    zoneAreas.forEach(area => drawPolygonArea(area));
    drawPolygonPoints(firstPoint);
    drawPolygonPoints(secondPoint);

    if (checkEdgeCollisionWithExposureZone(firstPoint, secondPoint, zonePolygons)) {
      return [[0, 1]];
    }

    // A part of the panel edge is covered by the roof boundary when it reaches a roof edge within
    // the exposure distance (0.5 * building height) toward this side, or lies in this side's zone
    // area. At a step in the roof boundary the edge that closes the gap is often tagged with another
    // zone, so every roof edge counts. Parts left uncovered must be covered by neighbour panels.
    coverage = coverageOfAreas(firstPoint, secondPoint, coverageAreas);
  }

  return coverage;
};

const SEGMENT_PARAM_TOLERANCE = 1e-9;
const BOUNDARY_TOLERANCE_PX = 1e-6;

type Point = { x: number, y: number };

export type ParamInterval = [number, number];

// A convex area with the inward unit normal of each side, and its bounding box.
type CoverageArea = {
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
  sides: { origin: Point, normal: Point }[],
};

type SideGeometry = {
  zoneAreas: Point[][],
  zonePolygons: PolygonInterface[],
  coverageAreas: CoverageArea[],
};

const SIDE_DIRECTIONS: { [side: string]: Point } = {
  N: { x: 0, y: -1 },
  S: { x: 0, y: 1 },
  E: { x: 1, y: 0 },
  W: { x: -1, y: 0 },
};

const cross = (a: Point, b: Point) => a.x * b.y - a.y * b.x;

const sub = (a: Point, b: Point) => ({ x: a.x - b.x, y: a.y - b.y });

// The roof zone object is memoized, so the areas built from it are kept until it is replaced.
const sideGeometryCache = new WeakMap<object, { [key: string]: SideGeometry }>();

const getSideGeometry = (
  roofZone: { edges: any[] },
  side: 'N' | 'S' | 'E' | 'W',
  distance: number,
): SideGeometry => {
  const key = `${side}:${distance}`;
  let cached = sideGeometryCache.get(roofZone);
  if (!cached) {
    cached = {};
    sideGeometryCache.set(roofZone, cached);
  }
  if (!cached[key]) {
    const zoneEdges = roofZone.edges.map(edge => edge.filter(corner => typeof corner === 'object'));
    const zoneAreas: Point[][] = zoneEdges.filter(edge => isEdgePoint(edge[2]) && edge[2].zone === side);
    const roofBoundarySegments: Point[][] = zoneEdges.map(edge => [edge[0], edge[1]]);
    cached[key] = {
      zoneAreas,
      zonePolygons: zoneAreas.map(area => new Polygon(area)),
      coverageAreas: toCoverageAreas([...zoneAreas, ...roofBoundaryReachAreas(roofBoundarySegments, side, distance)]),
    };
  }
  return cached[key];
};

// The panel edge lies inside one zone area of this side when both of its end points do.
export const checkEdgeCollisionWithExposureZone = (start: Point, end: Point, areas: PolygonInterface[]): boolean =>
  areas.some(area => area.containsPoint(start) && area.containsPoint(end));

// A point reaches a roof edge within `distance` toward the side exactly when it lies in that
// roof edge swept back against the side direction. Roof edges parallel to the direction are
// never reached.
export const roofBoundaryReachAreas = (
  roofBoundarySegments: Point[][],
  side: 'N' | 'S' | 'E' | 'W',
  distance: number,
): Point[][] => {
  const direction = SIDE_DIRECTIONS[side];
  const back = { x: -direction.x * distance, y: -direction.y * distance };
  return roofBoundarySegments
    .filter(([p, q]) => Math.abs(cross(sub(q, p), direction)) > SEGMENT_PARAM_TOLERANCE)
    .map(([p, q]) => [
      p,
      q,
      { x: q.x + back.x, y: q.y + back.y },
      { x: p.x + back.x, y: p.y + back.y },
    ]);
};

const toCoverageArea = (area: Point[]): CoverageArea | null => {
  if (area.length < 3) {
    return null;
  }
  const doubleArea = area.reduce((sum, point, index) => sum + cross(point, area[(index + 1) % area.length]), 0);
  if (Math.abs(doubleArea) <= SEGMENT_PARAM_TOLERANCE) {
    return null;
  }
  const orientation = doubleArea > 0 ? 1 : -1;
  const sides: CoverageArea['sides'] = [];
  area.forEach((origin, index) => {
    const side = sub(area[(index + 1) % area.length], origin);
    const sideLength = Math.sqrt(side.x * side.x + side.y * side.y);
    if (sideLength > SEGMENT_PARAM_TOLERANCE) {
      sides.push({ origin, normal: { x: -orientation * side.y / sideLength, y: orientation * side.x / sideLength } });
    }
  });
  const xs = area.map(point => point.x);
  const ys = area.map(point => point.y);
  return {
    sides,
    minX: Math.min(...xs) - BOUNDARY_TOLERANCE_PX,
    maxX: Math.max(...xs) + BOUNDARY_TOLERANCE_PX,
    minY: Math.min(...ys) - BOUNDARY_TOLERANCE_PX,
    maxY: Math.max(...ys) + BOUNDARY_TOLERANCE_PX,
  };
};

const toCoverageAreas = (areas: Point[][]): CoverageArea[] =>
  areas.map(toCoverageArea).filter((area): area is CoverageArea => area !== null);

// Part of the panel edge (as t in [0, 1]) inside a convex area, boundary included.
const coverageAreaInterval = (start: Point, end: Point, area: CoverageArea): ParamInterval | null => {
  if (
    Math.max(start.x, end.x) < area.minX ||
    Math.min(start.x, end.x) > area.maxX ||
    Math.max(start.y, end.y) < area.minY ||
    Math.min(start.y, end.y) > area.maxY
  ) {
    return null;
  }
  const direction = sub(end, start);
  let low = 0;
  let high = 1;
  for (const { origin, normal } of area.sides) {
    const offset = normal.x * (start.x - origin.x) + normal.y * (start.y - origin.y);
    const slope = normal.x * direction.x + normal.y * direction.y;
    if (Math.abs(slope) <= SEGMENT_PARAM_TOLERANCE) {
      if (offset < -BOUNDARY_TOLERANCE_PX) {
        return null;
      }
      continue;
    }
    const root = (-BOUNDARY_TOLERANCE_PX - offset) / slope;
    if (slope > 0) {
      low = Math.max(low, root);
    } else {
      high = Math.min(high, root);
    }
    if (high < low) {
      return null;
    }
  }
  return [low, high];
};

const coverageOfAreas = (start: Point, end: Point, areas: CoverageArea[]): ParamInterval[] => {
  const coverage: ParamInterval[] = [];
  areas.forEach((area) => {
    const interval = coverageAreaInterval(start, end, area);
    if (interval) {
      coverage.push(interval);
    }
  });
  return coverage;
};

export const getSegmentCoverage = (start: Point, end: Point, areas: Point[][]): ParamInterval[] =>
  coverageOfAreas(start, end, toCoverageAreas(areas));

const drawPolygonPoints = (point: { x: number, y: number }) => {
  if (!DEBUG.showExposureZonePolygonCheckEdge) {
    return;
  }

  const stage = getStage();
  const p = new PIXI.Graphics();
  p.id = 'pointHelper';
  p.beginFill(0xFF00FF);
  p.drawCircle(point.x, point.y, 1);
  p.zIndex = 100000;
  p.endFill();
  stage.addChild(p);
};

const drawPolygonArea = (area: { x: number, y: number }[]) => {
  if (!DEBUG.showExposureZoneCollisionArea) {
    return;
  }
  
  const stage = getStage();
  const p = new PIXI.Graphics();
  
  const pArea = area.reduce((acc, corner) => {
    acc.push(corner.x);
    acc.push(corner.y);
    return acc;
  },                        []);
  
  p.id = 'pointHelper';
  p.beginFill(0xFF00FF);
  p.drawPolygon(pArea);
  p.zIndex = 100000;
  p.endFill();
  stage.addChild(p);
};
